import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";

import { Editor } from "@craftjs/core";
import { AppContainer } from "twyne-new.ui";

import { GlobalStateProvider } from "@/components/builder/utils/State";
import { useBuilderStore, BuilderStoreProvider } from "@/components/builder/utils/store";
import { useDefaultTailwindConfig } from "@/components/builder/utils/tailwindConfig/hooks";
import BuilderContent from "@/modules/cms/views/post/components/builder/Content/Builder";
import LeftSidebar from "@/modules/cms/views/post/components/builder/Content/LeftSidebar";
import RightSidebar from "@/modules/cms/views/post/components/builder/Content/RightSidebar";
import Toolbar from "@/modules/cms/views/post/components/builder/Content/Toolbar";
import { BuilderProvider, useCraft, useEditing, useInteractionMode, useAutoSave } from "@/modules/cms/views/post/components/builder/context";
import useAutoSelectEditing from "@/modules/cms/views/post/components/builder/hooks/useAutoSelectEditing";
import useCompiler from "@/modules/cms/views/post/components/builder/hooks/useCompiler";
import { HistoryProvider } from "@/modules/cms/views/post/components/builder/hooks/useHistory";

import Header from "./Header";
import { BlockStoreProvider, useBlockStore } from "./store";

function Content() {
  useAutoSelectEditing()
  useDefaultTailwindConfig()

  return <GlobalStateProvider>
    <AppContainer direction='vertical'>
      <Header page={null} title={null} />
      <Toolbar history={null} versions={null} />
      <AppContainer className='relative p-2' variant='none'>
        <AppContainer direction='vertical' variant='none'>
          <BuilderContent />
        </AppContainer>
        <LeftSidebar defaultValue='assets' pages={null} />
        <RightSidebar />
      </AppContainer>
    </AppContainer>
  </GlobalStateProvider>
}

function Builder() {
  const { value: editing } = useEditing()
  const { value: interactionMode } = useInteractionMode()
  const { resolver } = useCompiler()
  const { setValue: setCraft, value: craft } = useCraft()
  const { handleAutoSave } = useAutoSave()

  useEffect(() => {
    if (craft && editing) {
      editing.definition.craft = craft
      handleAutoSave({ craft })
    }
  }, [editing, craft, handleAutoSave])

  const handleChange = useCallback(query => {
    const craft = query.getSerializedNodes()
    setCraft(craft)
  }, [setCraft])

  return <Editor resolver={resolver} onNodesChange={handleChange} enabled={interactionMode !== 'select'}>
    <HistoryProvider>
      <Content />
    </HistoryProvider>
  </Editor>
}

function BuilderContext() {
  const { postBuilder: { useLoad } } = useBuilderStore()
  const { id } = useParams()

  useLoad(id)

  return <BuilderProvider>
    <Builder />
  </BuilderProvider>
}

function BuilderStoreAdapter({ children }) {
  const store = useBlockStore()
  return <BuilderStoreProvider store={store}>
    {children}
  </BuilderStoreProvider>
}

export default function BuilderForm() {
  return <BlockStoreProvider>
    <BuilderStoreAdapter>
      <BuilderContext />
    </BuilderStoreAdapter>
  </BlockStoreProvider>
}
